import { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { Mail, Phone, MapPin, Send } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useContent, renderRich } from "@/context/ContentContext";

const initial = {
    name: "", email: "", company: "", phone: "",
    machine_type: "", production_volume: "", message: "",
};

const Contact = () => {
    const c = useContent();
    const ct = c.contact || {};
    const [form, setForm] = useState(initial);
    const [submitting, setSubmitting] = useState(false);
    const [done, setDone] = useState(false);
    const [params] = useSearchParams();

    useEffect(() => {
        const machine = params.get("machine");
        if (machine) {
            setForm((f) => ({ ...f, machine_type: machine, message: `Hello, I'd like a quote for: ${machine}\n\n` }));
        }
    }, [params]);

    const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });

    const submit = async (e) => {
        e.preventDefault();
        if (!form.name || !form.email || !form.message) {
            toast.error("Please complete name, email and message.");
            return;
        }
        try {
            setSubmitting(true);
            await api.post("/inquiries", form);
            setDone(true);
            setForm(initial);
            toast.success("Inquiry sent. Our team will be in touch shortly.");
        } catch {
            toast.error("Submission failed. Please try again.");
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div data-testid="contact-page">
            <section className="bg-gray-900 text-white">
                <div className="max-w-7xl mx-auto px-6 md:px-12 pt-24 md:pt-32 pb-20 grid md:grid-cols-12 gap-10">
                    <div className="md:col-span-7">
                        <div className="overline text-red-600 mb-6">{ct.hero_overline}</div>
                        <h1 className="font-display font-black uppercase tracking-tighter text-5xl md:text-7xl leading-[0.95]">
                            {renderRich(ct.hero_heading)}
                        </h1>
                    </div>
                    <div className="md:col-span-5">
                        <p className="text-gray-300 leading-relaxed">{ct.hero_paragraph}</p>
                    </div>
                </div>
            </section>

            <section className="max-w-7xl mx-auto px-6 md:px-12 py-20 md:py-24 grid md:grid-cols-12 gap-12">
                <aside className="md:col-span-4 space-y-10">
                    <div>
                        <div className="overline text-red-600 mb-3">{ct.email_label}</div>
                        <a className="text-gray-900 hover:text-red-600 inline-flex items-center gap-2" href={`mailto:${ct.email}`} data-testid="contact-email-link">
                            <Mail size={16} /> {ct.email}
                        </a>
                    </div>
                    <div>
                        <div className="overline text-red-600 mb-3">{ct.phone_label}</div>
                        <a className="text-gray-900 hover:text-red-600 inline-flex items-center gap-2" href={ct.phone_link || `tel:${ct.phone}`} data-testid="contact-phone-link">
                            <Phone size={16} /> {ct.phone}
                        </a>
                    </div>
                    <div>
                        <div className="overline text-red-600 mb-3">{ct.operations_label}</div>
                        <div className="text-gray-700 inline-flex items-center gap-2">
                            <MapPin size={16} /> {ct.operations}
                        </div>
                    </div>

                    <div className="bg-gray-50 border border-gray-200 p-6">
                        <div className="overline text-red-600 mb-3">{ct.lead_times_title}</div>
                        <ul className="text-sm text-gray-700 space-y-2">
                            {(ct.lead_times || []).map((t, i) => <li key={i}>{t}</li>)}
                        </ul>
                    </div>
                </aside>

                <div className="md:col-span-8">
                    {done ? (
                        <div className="border border-gray-200 p-10 md:p-14" data-testid="contact-success">
                            <div className="overline text-red-600 mb-4">Received</div>
                            <h2 className="font-display text-3xl md:text-4xl font-black uppercase tracking-tight mb-6">{ct.success_title}</h2>
                            <p className="text-gray-700">{ct.success_body}</p>
                            <button onClick={() => setDone(false)} className="btn-outline mt-8" data-testid="contact-send-another">Send Another</button>
                        </div>
                    ) : (
                        <form onSubmit={submit} className="space-y-6" data-testid="contact-form">
                            <div className="grid md:grid-cols-2 gap-6">
                                <div>
                                    <label className="label-mono">Full Name *</label>
                                    <input className="input-field" value={form.name} onChange={update("name")} data-testid="contact-input-name" />
                                </div>
                                <div>
                                    <label className="label-mono">Email *</label>
                                    <input type="email" className="input-field" value={form.email} onChange={update("email")} data-testid="contact-input-email" />
                                </div>
                                <div>
                                    <label className="label-mono">Company</label>
                                    <input className="input-field" value={form.company} onChange={update("company")} data-testid="contact-input-company" />
                                </div>
                                <div>
                                    <label className="label-mono">Phone</label>
                                    <input className="input-field" value={form.phone} onChange={update("phone")} data-testid="contact-input-phone" />
                                </div>
                                <div>
                                    <label className="label-mono">Machine / Line Type</label>
                                    <input className="input-field" value={form.machine_type} onChange={update("machine_type")} data-testid="contact-input-machine" placeholder="e.g. Vacuum sealer, Tube mill, Full line" />
                                </div>
                                <div>
                                    <label className="label-mono">Production Volume</label>
                                    <input className="input-field" value={form.production_volume} onChange={update("production_volume")} data-testid="contact-input-volume" placeholder="pcs/month" />
                                </div>
                            </div>
                            <div>
                                <label className="label-mono">Message *</label>
                                <textarea rows={6} className="input-field" value={form.message} onChange={update("message")} data-testid="contact-input-message" placeholder="Tell us about your factory, target output, and which machines you're considering." />
                            </div>
                            <div className="pt-2">
                                <button type="submit" className="btn-primary" disabled={submitting} data-testid="contact-submit">
                                    {submitting ? "Sending…" : (<>Send Inquiry <Send size={16} /></>)}
                                </button>
                            </div>
                        </form>
                    )}
                </div>
            </section>
        </div>
    );
};

export default Contact;
